import React, { useState, useEffect, useCallback, useRef, memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { setBusy, clearBusy } from 'irmlib/containers'
import { getYesterday } from 'irmlib/common'
import { FilterList, Close } from 'irmlib/icons'
import * as constants from 'irmlib/common/constants'
import { translate } from 'irmlib/multilanguage'
import { Paper, Typography, BasePopper, IconButton, ThemeButton } from 'irmlib/components/Controls'
import { ClearButton, DateTime, Select, Autocomplete, TextField, MultiSelectWithCheckboxes } from 'irmlib/components/FormControls'
import { FlexByRow, FloatRight, YDivider, XDivider, HalfScreen } from 'irmlib/styles'
import { useFormPageHandler, useMergeState } from 'irmlib/hooks'

import { SubRoutePage } from '../../AppControl/RoutePage'
import * as gdeTrancactions from '../../../services/firestore/gdeTransactions'
import { getUser } from '../../../redux/global'
import { useTranslation } from 'react-i18next'

const MultiSelectContainer = styled.div`
width: 310px; 
`

const Buttons = props => {
  return <>
    <ClearButton id={'filter__reset'} arrRefs={props.arrRefs} clearLabel={'buttons.Reset'} onClick={props.handleClear}/>
    <XDivider width='10px' />
    <ThemeButton id={'filter__search'} size={'medium'} color='primary' variant='contained' autoFocus onClick={props.handleClick} >
      forms.Search
    </ThemeButton>
  </>
}

Buttons.propTypes = {
  arrRefs: PropTypes.array,
  handleClick: PropTypes.func,
  handleClear: PropTypes.func
}

const VerticalDivider = (props) =>
  <YDivider height={props?.height || '50px'} />

VerticalDivider.propTypes = {
  height: PropTypes.string,
}

const defaultSelectOption = { value: constants.defaultSelect, label: constants.defaultSelect }

const initialSate = {
  showTable: false,
  countries: [],
  templates: [],
  templatesForSelect: [],
  userDetails: getUser(),
  wmsList: [],
  templateTypeList: [],
  selectedWms: '',
  selectedTemplate: '',
  centerList: [],
  districtsList: [],
  workersList: [],
  minDateOfToDate: getYesterday(),
  districtSelection: getUser().district,
}

const Filter = memo((props) => {
  const { t } = useTranslation()
  const anchorRef = useRef(null)
  const [open, setOpen] = React.useState(false)
  const [state, setState, getState] = useMergeState(initialSate)
  const [districtDisabledState, handleDistrictDisabled] = useState(false)
  const [appliedFilters, setFilters] = useState({})
  const [selectedCountry, setSelectedCountry] = useState(false)
  // const userDetails = getState().userDetails
  
  const id = open ? 'scroll-playground' : null

  const fieldsIndex = {
    country: 0,
    districtId: 1,
    startDate: 2,
    endDate: 3,
    accountLevel1: 4,
    allUsers: 5
  }

  const formTemplate = [
    {
      label: t('reports.filters.country'),
      defaultValue: appliedFilters.country,
    },
    {
      label: t('reports.filters.district'),
      defaultValue: appliedFilters.districtId,
    },
    {
      label: t('reports.filters.startDate'),
      defaultValue: appliedFilters.startDate, /* Default date is previous day */
    },
    {
      label: t('reports.filters.endDate'),
      defaultValue: appliedFilters.endDate, /* Default date is previous day */
    },
    {
      label: t('reports.filters.accountLevel'),
      defaultValue: appliedFilters?.accountLevel1?.toUpperCase(),
    },
    {
      label: t('reports.filters.employee'),
      defaultValue: appliedFilters.allUsers,
    },
  ]

  const [arrRefs, data] = useFormPageHandler({
    formTemplate
  })

  const searchClose = useCallback(() => {
    setOpen(false)
  })

  const getDistricts = async (country = '') => {
    setBusy()
    let collection
    if (country) {
      collection = await gdeTrancactions.getDistrictsByCountry(country)
    } else {
      collection = await gdeTrancactions.getDistricts()
    }
    const districtsList = []
    const centerList = []
    collection?.forEach((doc) => {
      // eslint-disable-next-line
      if (!centerList.includes(doc.data().center + '-ALL')) {
        // eslint-disable-next-line
        centerList.push(doc.data().center + '-ALL')
        // eslint-disable-next-line
        districtsList.push({ value: doc.data().center + '-ALL', label: doc.data().center + '-ALL' })
      }
      districtsList.push({ value: doc.data().districtName, label: doc.data().districtName })
    })
    setState({ centerList: centerList })
    setState({ districtsList: districtsList })
    clearBusy()
  }

  const getWorkersCollection = async (district = getUser().district) => {
    setBusy()
    // const collection = await gdeTrancactions.getWorkersWithDistrict(district)
    const collection = await gdeTrancactions.getworkersLists(district)
    const workersList = []
    // deafult selection
    workersList.push({ value: 'all-employees', label: translate('reports.filters.employeeDefaultSelection') })
    collection.forEach((doc) => {
      const { workerId, firstName, lastName } = doc.data()
      const name = `${firstName} ${lastName}-${workerId.replace(/\d(?=\d{3})/g, '*')}`
      workersList.push({ value: workerId, label: name })
    })
    
    setState({ workersList: workersList })
    clearBusy()
  }

  const handleFilterButtonClick = () => {
    setOpen((prevOpen) => {
      if (prevOpen === false) {
        const appliedFilters = { ...props.appliedFilters }
        Object.keys(appliedFilters).length > 0 && setFilters(appliedFilters)
      }
      return !prevOpen
    })
  }

  const getFilter = postObject => {
    setOpen(false)
    const appliedFilters = {}
    Object.entries(fieldsIndex).forEach((data) => {
      appliedFilters[data[0]] = arrRefs[data[1]].current?.getValue()
    })
    props.handleFilters(appliedFilters)
  }

  const clearFilters = () => {
    const filters = { ...appliedFilters }
    Object.keys(filters).forEach(filter => {
      filters[filter] = ''
      if (filter === 'districtId') {
        filters[filter] = []
      }
      if (filter === 'startDate' || filter === 'endDate') {
        filters[filter] = getYesterday()
      }
    })
    Object.keys(filters).forEach((key) => {
      const clearValue = ((props?.isEmployeeLevel && key !== 'accountLevel1') || (!props?.isEmployeeLevel && key !== 'allUsers'))
      if (clearValue) { 
        arrRefs[fieldsIndex[key]].current.setValue(filters[key]) 
      }
    })
    setFilters(filters)
  }

  const onChangeCountry = value => {
    if (value && value !== constants.defaultSelect && (!selectedCountry || selectedCountry !== value)) {
      setBusy()
      const selectedcountry = getState().countries.find(country => country.value === value)
      getDistricts(selectedcountry?.value)
      // clear district value when country value changed 
      arrRefs[fieldsIndex.districtId]?.current?.setValue([])
      setFilters((filters) => {
        filters.districtId = []
        return filters
      })
      handleDistrictDisabled(false)
      setSelectedCountry(value)
      clearBusy()
    }
  }
  
  const handleDistrictChange = (value) => {
    if (value && value.length > 0) {
      if (value.length === 1) {
        getWorkersCollection(value[0])
      } else {
        getWorkersCollection(value)
      }
    }
  }

  const getCountries = async () => {
    const collection = await gdeTrancactions.getCountries()
    const countryList = [defaultSelectOption]
    collection.forEach((doc) => {
      const countryName = doc.data().countryName
      countryList.push({ value: countryName, label: countryName })
    })
    setState({ countries: countryList })
  }

  const handleDateChange = (value) => {
    setState({
      minDateOfToDate: value
    })
    const endDate = arrRefs[fieldsIndex.endDate].current.getValue()
    const startTime = new Date(value).getTime()
    const endTime = new Date(endDate).getTime()
    if (endTime < startTime) {
      arrRefs[fieldsIndex.endDate].current.setValue(value)
    }
  }

  useEffect(() => {
    getCountries()
    getDistricts()
    getWorkersCollection()
  }, [])

  return (
    <>
      <IconButton
        ref={anchorRef}
        id={'filter_filterButton'}
        variant="contained"
        onClick={handleFilterButtonClick}
        aria-describedby={id}
      >
        <FilterList fontSize='medium' />
      </IconButton>
      <BasePopper
        id={id}
        open={open}
        anchorEl={anchorRef.current}
        placement={'top-end'}
        disablePortal={false}
        flip={true}
        preventOverflow={'disabled'}
        arrow={false}
        popperBody={
          <Paper elevation={3}>
            <FloatRight>
              <IconButton id={'filter_close'} onClick={searchClose}>
                <Close />
              </IconButton>
            </FloatRight>
            <SubRoutePage
              buttons={<Buttons arrRefs={arrRefs} handleClick={getFilter} handleClear={clearFilters}/>}
              displayTitle={false}
              centerButtons>
              <Typography>{t('reports.filters.title')}</Typography>
              <VerticalDivider />
              <FlexByRow>
                <HalfScreen>
                  <Select
                    id={'filter_country'}
                    onChange={(value) => { onChangeCountry(value) }}
                    data={data}
                    items={state.countries}
                    fieldTemplate={formTemplate[fieldsIndex.country]}
                    ref={arrRefs[fieldsIndex.country]}
                    noTranslateItems
                    noTranslate={true}
                  />  
                  <VerticalDivider />
                  
                  {props?.isEmployeeLevel
                    ? <Autocomplete
                        id={'filter_allUsers'}
                        data={data}
                        options={state.workersList}
                        fieldTemplate={formTemplate[fieldsIndex.allUsers]}
                        ref={arrRefs[fieldsIndex.allUsers]}
                        noTranslateItems
                        noTranslate={true}
                  />
                    : <TextField
                        id={'filter_accountLevel1'}
                        data={data}
                        fieldTemplate={formTemplate[fieldsIndex.accountLevel1]}
                        ref={arrRefs[fieldsIndex.accountLevel1]}
                        noTranslate={true}
                      />
                    
                 }
                 
                  <VerticalDivider />
                </HalfScreen>
                <XDivider width='50px' />
                <HalfScreen>
                  <MultiSelectContainer>
                    <MultiSelectWithCheckboxes 
                      id={'filter__district'}
                      label='reports.filters.district'
                      items={state.districtsList}
                      noTranslateItems={true}
                      fieldTemplate={formTemplate[fieldsIndex.districtId]}
                      ref={arrRefs[fieldsIndex.districtId]}
                      disabled={districtDisabledState}
                      onClose={handleDistrictChange}
                      districtsList={state?.districtsList}
                      centerList={state?.centerList}
                      appliedFilters={appliedFilters}
                      noTranslate={true}
                    />
                  </MultiSelectContainer>
                  <VerticalDivider />
                  <FlexByRow>
                    <DateTime
                      id={'filter__startDate'}
                      data={data}
                      fieldTemplate={formTemplate[fieldsIndex.startDate]}
                      onChange={handleDateChange}
                      ref={arrRefs[fieldsIndex.startDate]}
                      type='date'
                      noTranslate={true}
                    />
                    <XDivider width='20px' />
                    <DateTime
                      id={'filter__endDate'}
                      data={data}
                      fieldTemplate={formTemplate[fieldsIndex.endDate]}
                      ref={arrRefs[fieldsIndex.endDate]}
                      minDate={state.minDateOfToDate}
                      type='date'
                      noTranslate={true}
                    />
                  </FlexByRow>
                </HalfScreen>
              </FlexByRow>
            </SubRoutePage>
          </Paper>
        }
      />
    </>
  )
})
Filter.propTypes = {
  handleFilters: PropTypes.func,
  isEmployeeLevel: PropTypes.bool,
  appliedFilters: PropTypes.object
}
export default Filter
